import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import getSymbolFromCurrency from 'currency-symbol-map';
import React from 'react';
import languages from '../../../../../../helpers/utils/languages';
import SAVOrderIcon from '../../../../../svg-icons/SAVOrderIcon';

const SavConfirmModal = ({
  isOpen,
  handleClose,
  handleConfirm,
  formData,
  confirmLoading,
}) => {
  const handleCopy = () => {
    const textToCopy = `Numéro de commande : ${formData?.customerOrder}
Client : ${formData?.firstName} ${formData?.lastName}
Phone : ${formData?.phone}
Motif : ${formData?.reason}
Préparateur : ${formData?.orderPicker}

Liste produits :
${formData?.lines
  ?.map((line) => `${line.internalProduct} - Quantité: ${line.quantity}`)
  .join('\n')}

Adresse de livraison :
${formData?.firstName} ${formData?.lastName}
${formData?.shippingAddress?.address1}
${
  formData?.shippingAddress?.address2
    ? formData?.shippingAddress?.address2 + '\n'
    : ''
}${formData?.shippingAddress?.postalCode} ${formData?.shippingAddress?.city}
${
  formData?.shippingAddress?.country
    ? languages.find(
        (country) => country.code2 === formData.shippingAddress.country
      )?.country || formData.shippingAddress.country
    : ''
}
${formData?.phone}

Adresse de facturation :
${formData?.firstName} ${formData?.lastName}
${formData?.billingAddress?.address1}
${
  formData?.billingAddress?.address2
    ? formData?.billingAddress?.address2 + '\n'
    : ''
}${formData?.billingAddress?.postalCode} ${formData?.billingAddress?.city}
${
  formData?.billingAddress?.country
    ? languages.find(
        (country) => country.code2 === formData.billingAddress.country
      )?.country || formData.billingAddress.country
    : ''
}
${formData?.phone}

Commentaire :
${formData?.comment}`;

    navigator.clipboard.writeText(textToCopy);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          borderBottom: 1,
          marginBottom: 3,
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          fontSize: 24,
          color: 'primary.main',
        }}
      >
        <SAVOrderIcon fontSize="large" />
        Confirmation de la commande SAV
      </DialogTitle>

      <DialogContent
        sx={{
          height: '75vh',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" color="primary">
            Informations de la commande
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
            <Typography>
              <strong>Numéro de commande:</strong> {formData?.customerOrder}
            </Typography>

            <Typography>
              <strong>Client:</strong> {formData?.firstName}{' '}
              {formData?.lastName}
            </Typography>
            <Typography>
              <strong>Phone:</strong> {formData?.phone}
            </Typography>
            <Typography>
              <strong>Motif:</strong> {formData?.reason}
            </Typography>
            <Typography>
              <strong>Préparateur:</strong> {formData?.orderPicker}
            </Typography>
            {formData?.isPayable && (
              <Typography>
                <strong>Montant TTC net:</strong> {formData?.amountTtcNet}{' '}
                {getSymbolFromCurrency(formData.currency)}
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <strong>Liste des pièces / colis:</strong>
              {formData?.lines?.map((line) => (
                <Box
                  key={line?.internalProduct}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
                >
                  <Typography>
                    <strong>Produit interne:</strong> {line.internalProduct}
                  </Typography>
                  -
                  <Typography>
                    <strong>Quantité:</strong> {line.quantity}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Typography>
              <strong>Date de livraison souhaitée:</strong>{' '}
              {new Date(formData?.desiredShippingDate).toLocaleDateString()}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}>
              <Box>
                <Typography>
                  <strong>Adresse de livraison:</strong>
                </Typography>
                <Typography>
                  {formData?.shippingAddress?.firstName}{' '}
                  {formData?.shippingAddress?.lastName}
                </Typography>
                <Typography>{formData?.shippingAddress?.address1}</Typography>
                {formData?.shippingAddress?.address2 && (
                  <Typography>{formData?.shippingAddress?.address2}</Typography>
                )}
                <Typography>
                  {formData?.shippingAddress?.postalCode}{' '}
                  {formData?.shippingAddress?.city}
                </Typography>
                {formData?.shippingAddress?.country && (
                  <>
                    <span
                      className={`fi fi-${formData.shippingAddress.country.toLowerCase()}`}
                      style={{ marginRight: 8 }}
                    />
                    {languages.find(
                      (country) =>
                        country.code2 === formData.shippingAddress.country
                    )?.country || formData.shippingAddress.country}
                  </>
                )}
                <Typography>{formData?.shippingAddress?.phone}</Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography>
                  <strong>Adresse de facturation:</strong>
                </Typography>
                <Typography>
                  {formData?.billingAddress?.firstName}{' '}
                  {formData?.billingAddress?.lastName}
                </Typography>
                <Typography>{formData?.billingAddress?.address1}</Typography>
                {formData?.billingAddress?.address2 && (
                  <Typography>{formData?.billingAddress?.address2}</Typography>
                )}
                <Typography>
                  {formData?.billingAddress?.postalCode}{' '}
                  {formData?.billingAddress?.city}
                </Typography>
                <Typography>
                  {formData?.billingAddress?.country
                    ? languages.find(
                        (country) =>
                          country.code2 === formData.billingAddress.country
                      )?.country || formData.billingAddress.country
                    : ''}
                </Typography>
                <Typography>{formData?.billingAddress?.phone}</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />

            {formData?.comment?.length > 0 && (
              <>
                <Typography>
                  <strong>Commentaire:</strong>
                </Typography>
                <Typography
                  sx={{
                    fontStyle: 'italic',
                    padding: 2,
                    border: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: 1,
                  }}
                >
                  {formData?.comment}
                </Typography>
              </>
            )}

            {formData?.orderPicker === 'Yusen' && (
              <Typography>
                <span>
                  À la confirmation votre commande passera au statut{' '}
                  <b>Validée</b>. Veillez à bien vérifier les informations.
                </span>
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}
      >
        <Button
          variant="outlined"
          color="info"
          onClick={handleClose}
          sx={{ marginRight: 2 }}
        >
          Annuler
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCopy}
            endIcon={<ContentCopyIcon />}
          >
            Copier
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            endIcon={
              confirmLoading ? <CircularProgress size={10} /> : <CheckIcon />
            }
            disabled={confirmLoading}
          >
            Confirmer la commande
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SavConfirmModal;
